import { useId, useState } from 'react';
import { FullLevel } from '../../../api/types/compounds/FullLevel';
import { FormGroup, FormInputDescription, FormInputLabel } from '../../../components/form';
import { Heading2 } from '../../../components/headings';
import { URLInput } from '../../../components/shared/input/Input';
import { SecondaryButton } from '../../../components/ui/buttons';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import APIClient from '../../../api/APIClient';
import renderToastError from '../../../utils/renderToastError';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import ShowcaseRules from '../../../components/shared/ShowcaseRules';

interface Props {
    level: FullLevel;
}

export default function Showcase({ level }: Props) {
    if (level.Showcase === null) return <SuggestShowcase levelID={level.ID} />;

    return (
        <section className='mt-6'>
            <Heading2 className='mb-2' id='levelShowcase'>
                Showcase
            </Heading2>
            <LiteYouTubeEmbed
                thumbnail={`https://i.ytimg.com/vi/${level.Showcase}/maxresdefault.jpg`}
                id={level.Showcase}
                title={`Showcase video for ${level.Meta.Name}`}
                wrapperClass='yt-lite round:rounded-xl'
            />
        </section>
    );
}

function SuggestShowcase({ levelID }: { levelID: number }) {
    const [showForm, setShowForm] = useState(false);
    const [suggestion, setSuggestion] = useState('');
    const suggestionID = useId();

    const submitMutation = useMutation({
        mutationFn: (videoID: string) => APIClient.post(`/showcase`, { videoID, levelID }),
    });

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (submitMutation.isPending) return;

        const url = new URL(suggestion);
        let videoID: string | undefined;
        switch (url.host.toLowerCase()) {
            default: {
                return toast.error('Not a YouTube URL');
            }
            case 'www.youtube.com':
            case 'm.youtube.com':
            case 'youtube.com': {
                const pathname = url.searchParams.get('v');
                if (!pathname) return toast.error('Could not resolve video ID!');
                url.pathname = pathname;

                // Fallthrough
            }
            case 'youtu.be': {
                videoID = /^\/([a-zA-Z0-9_-]{11,})/.exec(url.pathname)?.[1];
                if (!videoID) return toast.error('Could not resolve video ID!');
            }
        }

        const handle = toast.loading('Sending...');
        submitMutation.mutate(videoID, {
            onSuccess: () => {
                toast.update(handle, {
                    render: 'Suggestion sent!',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });
                setShowForm(false);
                setSuggestion('');
            },
            onError: (error) => {
                toast.update(handle, {
                    render: renderToastError.render({ data: error }),
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                });
            },
        });
    }

    return (
        <section className='mt-6'>
            <Heading2 className='mb-2'>Showcase</Heading2>
            <p>
                No showcase yet,{' '}
                <button type='button' className='text-blue-500 underline-t' onClick={() => setShowForm(true)}>
                    suggest
                </button>{' '}
                one
            </p>
            {showForm && (
                <form onSubmit={handleSubmit} className='max-w-xl'>
                    <FormGroup>
                        <p className='font-bold'>What makes a good showcase</p>
                        <ShowcaseRules />
                        <FormInputDescription>
                            Suggestions that don't meet these are unlikely to be accepted.
                        </FormInputDescription>
                    </FormGroup>
                    <FormGroup>
                        <FormInputLabel htmlFor={suggestionID}>YouTube URL</FormInputLabel>
                        <div className='flex flex-col gap-2 sm:flex-row sm:items-start'>
                            <div className='sm:flex-1'>
                                <URLInput
                                    id={suggestionID}
                                    value={suggestion}
                                    onChange={(e) => setSuggestion(e.target.value)}
                                    required
                                />
                                <FormInputDescription>
                                    Please provide a YouTube video URL, e.g. <code>https://youtu.be/abc123</code>.
                                </FormInputDescription>
                            </div>
                            <SecondaryButton type='submit'>Send</SecondaryButton>
                        </div>
                    </FormGroup>
                </form>
            )}
        </section>
    );
}
